import { useCallback, useEffect, useState } from 'react';
import { getOrg, setTrialEnd, PAGE_LIMIT, listOrgs, createOrg, cancelSubscription, pauseSubscription, resumeSubscription, forceSync, getStripeLink, linkStripe, extendStripeTrial, endStripeTrial, clearBillingOverride } from '../api/orgs';
import { setPlan } from '../api/orgs';
import { apiDelete } from '../api';
import { formatDateTime } from '../utils/formatDateTime';
import { useI18n } from '../i18n/context';
import OrganizationRowCard from '../components/organizations/OrganizationRowCard';
import ListToolbar from '../ui/ListToolbar';
import ModalShell from '../ui/ModalShell';
import DateTimeSidePicker from '../ui/DateTimeSidePicker';
import SetTrialEndModal from '../ui/SetTrialEndModal';
import { loadState, persistState, resetState } from '../utils/statePersistence';
import '../styles/ui.css';
import './OrganizationsNew.css';

export default function Organizations() {
  const { t } = useI18n();
  const defaults = { q: '', page: 1 };
  const { state: persisted } = loadState('sa-orgs-state', defaults, {
    q: { param: 'search' },
    page: { param: 'page', parse: (v) => Math.max(1, parseInt(v, 10) || 1), serialize: (v) => String(v) }
  });
  const [list, setList] = useState([]);
  const [page, setPage] = useState(persisted.page);
  const [q, setQ] = useState(persisted.q);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [detail, setDetail] = useState(null); // {org, actions}

  useEffect(() => {
    if (!toast?.msg) return undefined;
    const text = String(toast.msg);
    const longMessage = text.length > 140;
    const duration = toast.err
      ? (longMessage ? 10000 : 8000)
      : (longMessage ? 9000 : 5500);
    const id = window.setTimeout(() => setToast(null), duration);
    return () => window.clearTimeout(id);
  }, [toast]);

  const [actionBusy, setActionBusy] = useState({});
  const [trialModal, setTrialModal] = useState(null); // { orgId, endsAt, reason, saving, error }
  const [endTrialModal, setEndTrialModal] = useState(null); // { orgId, orgName, reason, saving, error }
  const [deleteModal, setDeleteModal] = useState(null); // { id, name }
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteConfirmAck, setDeleteConfirmAck] = useState(false);
  const [createModal, setCreateModal] = useState(null); // { name, billing_tier, admin_email, admin_password, admin_password2, admin_name, saving, error }
  const [planModal, setPlanModal] = useState(null); // { orgId, plan, reason, seat_limit, trial_days, saving, error }
  const [stripeModal, setStripeModal] = useState(null); // { orgId, stripe_customer_id, stripe_subscription_id, saving, error }
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  const upsertOrg = (org) => {
    if (!org || !org.id) return;
    setList((prev) => {
      const exists = prev.some((o) => o.id === org.id);
      if (exists) return prev.map((o) => (o.id === org.id ? { ...o, ...org } : o));
      return [org, ...prev];
    });
    if (detail?.organization?.id === org.id) {
      setDetail((d) => d && ({ ...d, organization: { ...d.organization, ...org } }));
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
        const data = await listOrgs({ q, page, limit: PAGE_LIMIT });
        const items = data.organizations || data.data || data || [];
        if (page > 1 && Array.isArray(items) && items.length === 0) {
          setPage(1);
          return;
        }
        setList(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error('Failed to load organizations:', e);
      setError(e?.message || 'Error en la solicitud');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const triggerAction = async (orgId, action) => {
    setActionBusy((s) => ({ ...s, [orgId]: true }));
    try {
      let resultOrg = null;
      let toastMsg = null;
      const targetOrg = list.find((o) => o.id === orgId) || detail?.organization || null;
      const stripeStatus = (targetOrg?.stripe_subscription_status || '').toLowerCase();
      const stripeTrialBlocked = !targetOrg?.stripe_subscription_id || ['canceled', 'incomplete_expired', 'unpaid'].includes(stripeStatus);
      if (action === 'cancelSub') {
        if (!window.confirm(t('org.confirmCancelSub') || 'Cancel this organization’s subscription?')) return;
        const res = await cancelSubscription(orgId);
        resultOrg = res?.organization || res;
        toastMsg = t('org.toastCancelled') || 'Subscription cancelled';
      } else if (action === 'pauseSub') {
        if (!window.confirm(t('org.confirmPauseSub') || 'Pause billing for this organization?')) return;
        const res = await pauseSubscription(orgId);
        resultOrg = res?.organization || res;
        toastMsg = t('org.toastPaused') || 'Subscription paused';
      } else if (action === 'resumeSub') {
        if (!window.confirm(t('org.confirmResumeSub') || 'Resume billing for this organization?')) return;
        const res = await resumeSubscription(orgId);
        resultOrg = res?.organization || res;
        toastMsg = t('org.toastResumed') || 'Subscription resumed';
      } else if (action === 'forceSync') {
        const res = await forceSync(orgId);
        resultOrg = res?.organization || res;
        toastMsg = t('org.toastSynced') || 'Billing synchronized';
      } else if (action === 'openStripe') {
        const res = await getStripeLink(orgId);
        if (res?.url) {
          const accountId = res?.accountId || '';
          const live = res?.live === true;
          const customerId = res?.customerId || '';
          if (accountId || customerId) {
            const isTest = !live; // true = test mode, false = live mode
            const envName = window.location.hostname.includes('sbx') ? 'SBX' : 
                           window.location.hostname.includes('staging') ? 'STAGING' : 'PROD';
            
            const stripeInfo = `
🏭 INFORMACIÓN STRIPE - ${envName}
═══════════════════════════════════════

📊 CUENTA:
• ID: ${accountId || 'No disponible'}
• Modo: ${isTest ? '🧪 TEST (pruebas)' : '🔴 LIVE (producción)'}
• Entorno: ${envName}

👤 CLIENTE:
• ID: ${customerId || 'No disponible'}
• URL Dashboard: ${res.url}

⚠️  INSTRUCCIONES IMPORTANTES:
${isTest ? 
  `• Estás en modo TEST - usa tarjetas de prueba
• Las transacciones NO son reales
• Tarjetas de prueba: 4242 4242 4242 4242` :
  `• Estás en modo LIVE - las transacciones son REALES
• El dinero real será procesado`
}

🔧 SOLUCIÓN DE PROBLEMAS:
• Si Stripe dice "No such customer":
  → Verifica que estás en la cuenta Stripe correcta
  → En el dashboard de Stripe, cambia al workspace/cuenta: ${accountId}
  → Asegúrate de estar en el modo ${isTest ? 'TEST' : 'LIVE'} correcto

• Si no ves el cliente:
  → Revisa el Customer ID: ${customerId}
  → Verifica el entorno: ${envName}
  → Confirma el modo Stripe: ${isTest ? 'TEST' : 'LIVE'}

📋 DATOS TÉCNICOS:
• Backend URL: ${window.location.origin}
• Account ID: ${accountId}
• Customer ID: ${customerId}
• Live Mode: ${live}
• Environment: ${envName}
            `.trim();
            
            window.alert(stripeInfo);
          }
          window.open(res.url, '_blank', 'noopener');
        }
        return;
      } else if (action === 'editStripe') {
        setStripeModal({
          orgId,
          stripe_customer_id: targetOrg?.stripe_customer_id || '',
          stripe_subscription_id: targetOrg?.stripe_subscription_id || '',
          saving: false,
          error: ''
        });
        return;
      } else if (action === 'grace7' || action === 'grace14' || action === 'grace30') {
        if (stripeTrialBlocked) {
          setToast({ msg: t('orgActions.stripeTrialBlocked') || 'Stripe no permite modificar el trial en este estado', err: true });
          return;
        }
        const days = action === 'grace7' ? 7 : action === 'grace14' ? 14 : 30;
        const reason = window.prompt(t('billingOverride.promptReason') || 'Motivo (opcional)', 'Stripe trial extension') || '';
        const res = await extendStripeTrial(orgId, { days, reason });
        if (res?.stripe_sync && res.stripe_sync.success === false) {
          throw new Error(res?.stripe_sync?.reason || res?.stripe_sync?.error || 'Stripe sync failed');
        }
        resultOrg = res?.organization || res;
        toastMsg = res?.stripe_sync?.stripe_trial_update_skipped
          ? (t('orgActions.extendStripeTrialDbOnlyToast') || res?.stripe_sync?.hint || '')
          : (t('orgActions.extendStripeTrialToast') || 'Trial de Stripe actualizado');
      } else if (action === 'endStripeTrial') {
        if (stripeTrialBlocked) {
          setToast({ msg: t('orgActions.stripeTrialBlocked') || 'Stripe no permite modificar el trial en este estado', err: true });
          return;
        }
        const org = list.find((o) => o.id === orgId) || detail?.organization;
        setEndTrialModal({
          orgId,
          orgName: org?.name || '',
          reason: '',
          saving: false,
          error: ''
        });
        return;
      } else if (action === 'clearBillingOverride') {
        if (!window.confirm(t('billingOverride.confirmClear') || 'Quitar override manual?')) return;
        const reason = window.prompt(t('billingOverride.promptReason') || 'Motivo (opcional)', 'Clear override') || '';
        const cleared = await clearBillingOverride(orgId, { reason });
        const synced = await forceSync(orgId);
        resultOrg = synced?.organization || cleared?.organization || synced || cleared;
        toastMsg = t('billingOverride.toastCleared') || 'Override quitado';
      } else if (action === 'setTrial') {
        if (stripeTrialBlocked) {
          setToast({ msg: t('orgActions.stripeTrialBlocked') || 'Stripe no permite modificar el trial en este estado', err: true });
          return;
        }
        const org = list.find((o) => o.id === orgId);
        setTrialModal({
          orgId,
          endsAt: org?.trial_ends_at ? org.trial_ends_at.slice(0, 16) : '',
          reason: '',
          saving: false,
          error: ''
        });
        return;
      } else if (action === 'setPlan') {
        const org = list.find((o) => o.id === orgId);
        setPlanModal({
          orgId,
          plan: org?.plan || 'starter',
          seat_limit: org?.seat_limit || '',
          trial_days: '',
          reason: '',
          saving: false,
          error: ''
        });
        return;
      }
      if (resultOrg) upsertOrg(resultOrg);
      setToast({ msg: toastMsg || t('common.saved') || 'Guardado', err: false });
      await load();
    } catch (e) {
      const detail = e?.data?.detail || e?.data?.stripe_sync?.error || e?.data?.stripe_sync?.reason || null;
      setToast({ msg: detail || e?.data?.error || e.message || 'Error', err: true });
    } finally {
      setActionBusy((s) => {
        const next = { ...s };
        delete next[orgId];
        return next;
      });
    }
  };

  useEffect(() => {
    persistState('sa-orgs-state', { q, page }, defaults, {
      q: { param: 'search' },
      page: { param: 'page', serialize: (v) => String(v) }
    });
  }, [q, page]);

  useEffect(() => { load(); /* eslint-disable react-hooks/exhaustive-deps */ }, [page, q]);

  useEffect(() => {
    if (!deleteModal) return;
    setDeleteConfirmName('');
    setDeleteConfirmAck(false);
    setDeleteError('');
  }, [deleteModal]);

  const safeT = useCallback((key, fallback = '') => {
    const v = t(key);
    return v === key ? (fallback || key) : v;
  }, [t]);

  const planLabel = useCallback((plan) => safeT(`org.planLabelMap.${plan}`, plan || '—').toUpperCase(), [safeT]);
  const statusLabel = useCallback((status) => {
    const s = (status || '').toLowerCase();
    if (s === 'active') return safeT('org.statusActive', 'activo');
    if (s === 'suspended') return safeT('org.statusSuspended', 'suspendida');
    if (s === 'paused') return safeT('org.statusPaused', 'pausada');
    return status || '—';
  }, [safeT]);
  const seatLabel = safeT('org.usersLabel', 'usuarios');
  const formatSeatLimit = (count) => safeT('org.planUsersLimit', '{count} {label}')
    .replace('{count}', count)
    .replace('{label}', seatLabel);
  const planOptions = [
    { value: 'starter', limit: 5 },
    { value: 'growth', limit: 15 },
    { value: 'business', limit: 30 },
    { value: 'enterprise', limit: 60 }
  ];

  const fetchDetail = async (orgId) => {
    try {
      const data = await getOrg(orgId);
      setDetail(data);
    } catch (e) {
      setToast({ msg: 'Error loading details', err: true });
    }
  };

  const deleteNameExpected = (deleteModal?.name || '').trim();
  const deleteNameMatches = deleteNameExpected.length > 0 && deleteConfirmName.trim() === deleteNameExpected;
  const deleteAllowed = deleteConfirmAck && deleteNameMatches && !deleteSaving;

  const canNext = list.length === PAGE_LIMIT;
  const canPrev = page > 1;

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('org.title')}</h1>
        <p className="sa-page-subtitle">{t('org.subtitle')}</p>
      </div>
      <div className="ui-page-toolbar">
        <ListToolbar
          title={null}
          subtitle={null}
          searchValue={q}
          onSearchChange={(v) => { setQ(v); setPage(1); }}
          searchPlaceholder={t('org.searchPlaceholder')}
          actions={(
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="ui-button ghost" onClick={() => setCreateModal({ name: '', billing_tier: 'starter', admin_email: '', admin_password: '', admin_password2: '', admin_name: '', saving: false, error: '' })}>
                {t('org.createNew') || 'Nueva organización'}
              </button>
              <button className="ui-button primary" onClick={() => { setPage(1); load(); }}>
                {t('common.search')}
              </button>
            </div>
          )}
          filters={[
            <button
              key="reset"
              className="ui-button ghost"
              type="button"
              onClick={() => {
                const next = resetState('sa-orgs-state', defaults);
                setQ(next.q);
                setPage(next.page);
              }}
            >
              {t('common.reset')}
            </button>
          ]}
        />
      </div>

      <div className="ui-page-content">
        {error && (
          <div className="error-box">
            {t('common.error')}: {error}
          </div>
        )}

        {loading ? (
          <div className="ui-empty">{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div className="ui-empty">{t('org.empty')}</div>
        ) : selectedOrgId && list.find(o => o.id === selectedOrgId) ? (
          <>
            <OrganizationRowCard
              key={selectedOrgId}
              org={list.find(o => o.id === selectedOrgId)}
              onAction={(key) => {
                if (key === 'showActionsInline') return;
                if (key === 'viewDetails') return fetchDetail(selectedOrgId);
                if (key === 'delete') return setDeleteModal({ id: selectedOrgId, name: list.find(o => o.id === selectedOrgId)?.name });
                if (key === 'setPlan') return triggerAction(selectedOrgId, 'setPlan');
                triggerAction(selectedOrgId, key);
              }}
              showActionsInline={true}
            />
            <div style={{ marginTop: 24 }}>
              <button className="ui-button ghost" onClick={() => setSelectedOrgId(null)}>{t('actions.back') || 'Volver'}</button>
            </div>
          </>
        ) : (
          <div className="ui-list">
            {list.map((org) => (
              <OrganizationRowCard
                key={org.id}
                org={org}
                onAction={(key) => {
                  if (key === 'showActionsInline') return setSelectedOrgId(org.id);
                  if (key === 'viewDetails') return fetchDetail(org.id);
                  if (key === 'delete') return setDeleteModal({ id: org.id, name: org.name });
                  if (key === 'setPlan') return triggerAction(org.id, 'setPlan');
                  triggerAction(org.id, key);
                }}
                showActionsInline={false}
              />
            ))}
          </div>
        )}

        <div className="pagination">
          <button disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>{t('common.previous')}</button>
          <span>{t('common.page')} {page}</span>
          <button disabled={!canNext} onClick={() => setPage((p) => p + 1)}>{t('common.next')}</button>
        </div>

        {toast && (
          <div
            className={`toast ${toast.err ? 'error' : ''}`}
            role="status"
            aria-live="polite"
          >
            {toast.msg}
          </div>
        )}
      </div>

      {detail && (
        <ModalShell
          title={detail.organization?.name || ''}
          onClose={() => setDetail(null)}
          footer={
            <button className="ui-button ghost" onClick={() => setDetail(null)}>
              {t('actions.close') || 'Cerrar'}
            </button>
          }
        >
          <div className="ui-field">
            <div className="ui-label">{t('orgCards.accessTitle')}</div>
            <div>
              {planLabel(detail.organization?.effective_access?.effective_plan || detail.organization?.plan)}
              {' · '}
              {statusLabel(detail.organization?.effective_access?.effective_status || detail.organization?.access_status)}
              {detail.organization?.effective_access?.effective_until
                ? ` · ${safeT('orgCards.until', 'hasta {date}').replace('{date}', formatDateTime(detail.organization?.effective_access?.effective_until))}`
                : ''}
            </div>
          </div>
          <div className="drawer-grid drawer-grid--single">
            <div>
              <div className="drawer-label">{t('orgCards.planLabel')}</div>
              <div className="drawer-value">{planLabel(detail.organization?.plan || '—')}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgCards.planSource')}</div>
              <div className="drawer-value">{detail.organization?.plan_source || '—'}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgCards.status')}</div>
              <div className="drawer-value">{statusLabel(detail.organization?.access_status || '—')}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgActions.stripeCustomerId') || 'Stripe Customer ID'}</div>
              <div className="drawer-value">{detail.organization?.stripe_customer_id || '—'}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgActions.stripeSubscriptionId') || 'Stripe Subscription ID'}</div>
              <div className="drawer-value">{detail.organization?.stripe_subscription_id || '—'}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgActions.stripeStatus') || 'Stripe status'}</div>
              <div className="drawer-value">{detail.organization?.stripe_subscription_status || '—'}</div>
            </div>
            <div>
              <div className="drawer-label">{t('billingOverride.finalStatus') || 'Billing final'}</div>
              <div className="drawer-value">
                {detail.organization?.billing?.status
                  ? `${safeT(`billingOverride.status.${detail.organization.billing.status}`, detail.organization.billing.status)} · ${safeT(`billingOverride.source.${detail.organization.billing.source}`, detail.organization.billing.source)}${detail.organization.billing.override_active && detail.organization.billing.override_until ? ` · ${safeT('billingOverride.until', 'hasta {date}').replace('{date}', formatDateTime(detail.organization.billing.override_until))}` : ''}`
                  : '—'}
              </div>
            </div>
            <div>
              <div className="drawer-label">{t('orgCards.trialLabel')}</div>
              <div className="drawer-value">{formatDateTime(detail.organization?.trial_end_at || detail.organization?.trial_ends_at)}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgCards.graceLabel')}</div>
              <div className="drawer-value">{formatDateTime(detail.organization?.grace_ends_at)}</div>
            </div>
            <div>
              <div className="drawer-label">{t('orgCards.overrideLabel')}</div>
              <div className="drawer-value">
                {detail.organization?.override_plan
                  ? `${detail.organization.override_plan} · ${t('orgCards.until', { date: formatDateTime(detail.organization.override_ends_at) })}`
                  : t('orgCards.none')}
              </div>
            </div>
          </div>
          <div className="drawer-section">
            <h4>{t('orgCards.historyTitle')}</h4>
            <div className="actions-table-wrapper">
              <table className="actions-table">
                <thead>
                  <tr><th>{t('orgCards.colDate')}</th><th>{t('orgCards.colAction')}</th><th>{t('orgCards.colReason')}</th></tr>
                </thead>
                <tbody>
                  {(detail.admin_actions || []).map((a) => (
                    <tr key={a.id}>
                      <td>{formatDateTime(a.created_at)}</td>
                      <td>{a.action}</td>
                      <td>{a.reason || '—'}</td>
                    </tr>
                  ))}
                  {(!detail.admin_actions || detail.admin_actions.length === 0) && (
                    <tr><td colSpan={3}>{t('orgCards.noAudit')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </ModalShell>
      )}

      {planModal && (
        <ModalShell
          title={t('org.planChangeTitle') || 'Cambiar plan'}
          onClose={() => planModal.saving ? null : setPlanModal(null)}
          footer={(
            <>
              <button className="ui-button ghost" onClick={() => setPlanModal(null)} disabled={planModal.saving}>
                {t('actions.cancel')}
              </button>
              <button
                className="ui-button primary"
                onClick={async () => {
                  if (!planModal.plan) {
                    setPlanModal((m) => ({ ...m, error: t('org.planRequired') || 'Selecciona un plan' }));
                    return;
                  }
                  setPlanModal((m) => ({ ...m, saving: true, error: '' }));
                  try {
                    await setPlan(planModal.orgId, {
                      plan: planModal.plan,
                      reason: planModal.reason,
                      seat_limit: planModal.seat_limit ? Number(planModal.seat_limit) : undefined,
                      trial_days: planModal.trial_days ? Number(planModal.trial_days) : undefined
                    });
                    setPlanModal(null);
                    setToast({ msg: t('common.saved') || 'Guardado', err: false });
                    await load();
                  } catch (e) {
                    setPlanModal((m) => ({ ...m, saving: false, error: e?.data?.error || e.message || 'Error' }));
                  }
                }}
                disabled={planModal.saving}
              >
                {planModal.saving ? t('common.loading') : t('actions.save')}
              </button>
            </>
          )}
        >
          <div className="ui-field">
            <label className="ui-label">{t('org.planLabel')}</label>
            <select
              className="ui-input"
              value={planModal.plan}
              onChange={(e) => setPlanModal((m) => ({ ...m, plan: e.target.value }))}
            >
              {['starter', 'growth', 'business', 'enterprise'].map((p) => (
                <option key={p} value={p}>{t(`org.planLabelMap.${p}`)}</option>
              ))}
            </select>
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.planUsersLabel')}</label>
            <input
              className="ui-input"
              type="number"
              min="1"
              value={planModal.seat_limit ?? ''}
              onChange={(e) => setPlanModal((m) => ({ ...m, seat_limit: e.target.value }))}
              placeholder={t('org.planUsersPlaceholder')}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.planTrialDaysLabel')}</label>
            <input
              className="ui-input"
              type="number"
              min="1"
              max="365"
              value={planModal.trial_days ?? ''}
              onChange={(e) => setPlanModal((m) => ({ ...m, trial_days: e.target.value }))}
              placeholder={t('org.planTrialDaysPlaceholder')}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.planReasonLabel')}</label>
            <textarea
              className="ui-textarea"
              value={planModal.reason}
              placeholder={t('org.planReasonPlaceholder')}
              onChange={(e) => setPlanModal((m) => ({ ...m, reason: e.target.value }))}
            />
          </div>
          {planModal.error && <div className="error-box">{planModal.error}</div>}
        </ModalShell>
      )}

      {deleteModal && (
        <ModalShell
          title={t('orgDeleteModal.title') || 'Eliminar organización'}
          width="420px"
          onClose={() => !deleteSaving && setDeleteModal(null)}
          footer={(
            <div className="sa-danger-footer">
              <button type="button" className="ui-button secondary" onClick={() => setDeleteModal(null)} disabled={deleteSaving}>
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                className="ui-button danger"
                onClick={async () => {
                  if (!deleteModal?.id || deleteSaving || !deleteAllowed) return;
                  setDeleteSaving(true);
                  setDeleteError('');
                  setDeleteModal((m) => m && { ...m, closing: true });
                  try {
                    await apiDelete(`/superadmin/organizations/${deleteModal.id}`);
                    setList((prev) => prev.filter((o) => o.id !== deleteModal.id));
                    if (selectedOrgId === deleteModal.id) setSelectedOrgId(null);
                    setToast({ msg: t('org.deleted') || 'Organización eliminada', err: false });
                    setDeleteModal(null);
                    await load();
                  } catch (e) {
                    // Si el backend responde 404/410 o alreadyDeleted, lo tratamos como éxito idempotente
                    const alreadyGone = e?.status === 404 || e?.status === 410 || e?.data?.alreadyDeleted;
                    if (alreadyGone) {
                      setList((prev) => prev.filter((o) => o.id !== deleteModal.id));
                      if (selectedOrgId === deleteModal.id) setSelectedOrgId(null);
                      setToast({ msg: t('org.deleted') || 'Organización eliminada', err: false });
                      setDeleteModal(null);
                      await load();
                    } else {
                      setDeleteModal((m) => m && { ...m, closing: false });
                      setDeleteError(e?.message || 'Error al eliminar');
                      setToast({ msg: e?.message || 'Error al eliminar', err: true });
                    }
                  } finally {
                    setDeleteSaving(false);
                  }
                }}
                disabled={!deleteAllowed}
              >
                {deleteSaving ? (t('common.loading')) : (t('orgDeleteModal.confirmDelete') || 'Eliminar definitivamente')}
              </button>
            </div>
          )}
        >
          <div className="sa-danger-modal">
            <div className="sa-danger-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2 1 21h22L12 2zm0 6.8 5.4 9.2H6.6L12 8.8zm-1 2.2h2v4h-2v-4zm0 5.6h2v2h-2v-2z" />
              </svg>
            </div>
            <div className="sa-danger-warning">
              <div className="sa-danger-warning-title">
                {t('orgDeleteModal.warningTitle') || '⚠️ Esta acción es irreversible.'}
              </div>
              <div>{t('orgDeleteModal.warningIntro') || 'Se eliminarán:'}</div>
              <ul className="sa-danger-list">
                <li>{t('orgDeleteModal.itemOrg') || 'La organización'}</li>
                <li>{t('orgDeleteModal.itemUsers') || 'Todos los usuarios'}</li>
                <li>{t('orgDeleteModal.itemTime') || 'Todos los registros de tiempo'}</li>
                <li>{t('orgDeleteModal.itemData') || 'Toda la información asociada'}</li>
              </ul>
            </div>
            <div className="sa-danger-confirm">
              <label className="ui-label">{t('orgDeleteModal.confirmTitle') || 'Confirmación'}</label>
              <div className="sa-danger-hint">
                {(t('orgDeleteModal.orgNameHint') || 'Nombre de la organización: {name}').replace('{name}', deleteModal?.name || '—')}
              </div>
              <input
                className="ui-input sa-danger-input"
                placeholder={t('orgDeleteModal.placeholder') || 'Escribe el nombre de la organización para confirmar'}
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                disabled={deleteSaving}
              />
            </div>
            <label className="sa-danger-checkbox">
              <input
                type="checkbox"
                checked={deleteConfirmAck}
                onChange={(e) => setDeleteConfirmAck(e.target.checked)}
                disabled={deleteSaving}
              />
              <span>{t('orgDeleteModal.acknowledge') || 'Entiendo que esta acción no se puede deshacer'}</span>
            </label>
          </div>
          {deleteError && <div className="sa-toast sa-toast--error" style={{ position: 'relative', marginTop: 8 }}>{deleteError}</div>}
        </ModalShell>
      )}

      {stripeModal && (
        <ModalShell
          title={t('orgActions.configureStripe') || 'Configurar Stripe'}
          onClose={() => stripeModal.saving ? null : setStripeModal(null)}
          footer={(
            <>
              <button className="ui-button ghost" onClick={() => setStripeModal(null)} disabled={stripeModal.saving}>
                {t('actions.cancel')}
              </button>
              <button
                className="ui-button primary"
                onClick={async () => {
                  const customerId = (stripeModal.stripe_customer_id || '').trim();
                  const subscriptionId = (stripeModal.stripe_subscription_id || '').trim();
                  if (!customerId && !subscriptionId) {
                    setStripeModal((m) => ({ ...m, error: t('orgActions.stripeIdsRequired') || 'Ingresa Customer o Subscription' }));
                    return;
                  }
                  setStripeModal((m) => ({ ...m, saving: true, error: '' }));
                  try {
                    const res = await linkStripe(stripeModal.orgId, {
                      stripe_customer_id: customerId || null,
                      stripe_subscription_id: subscriptionId || null
                    });
                    const updated = res?.organization || res;
                    if (updated?.id) {
                      upsertOrg(updated);
                      setStripeModal(null);
                      setToast({ msg: t('orgActions.stripeLinked') || 'Stripe actualizado', err: false });
                    } else {
                      throw new Error('Respuesta inválida');
                    }
                  } catch (e) {
                    setStripeModal((m) => ({ ...m, saving: false, error: e?.data?.error || e?.message || 'Error' }));
                  }
                }}
                disabled={stripeModal.saving}
              >
                {stripeModal.saving ? (t('common.saving') || 'Guardando...') : (t('actions.save') || 'Guardar')}
              </button>
            </>
          )}
        >
          <div className="ui-field">
            <div className="ui-label">{t('orgActions.stripeCustomerId') || 'Stripe Customer ID'}</div>
            <input
              className="ui-input"
              placeholder="cus_..."
              value={stripeModal.stripe_customer_id}
              onChange={(e) => setStripeModal((m) => ({ ...m, stripe_customer_id: e.target.value }))}
              disabled={stripeModal.saving}
            />
          </div>
          <div className="ui-field">
            <div className="ui-label">{t('orgActions.stripeSubscriptionId') || 'Stripe Subscription ID'}</div>
            <input
              className="ui-input"
              placeholder="sub_..."
              value={stripeModal.stripe_subscription_id}
              onChange={(e) => setStripeModal((m) => ({ ...m, stripe_subscription_id: e.target.value }))}
              disabled={stripeModal.saving}
            />
          </div>
          {stripeModal.error && <div className="ui-error">{stripeModal.error}</div>}
        </ModalShell>
      )}

      {trialModal && (
        <ModalShell
          title={t('orgTrialModal.title')}
          width="500px"
          style={{ minHeight: '520px' }}
          onClose={() => trialModal.saving ? null : setTrialModal(null)}
          footer={null}
        >
          <SetTrialEndModal
            initialDate={trialModal.endsAt ? new Date(trialModal.endsAt) : new Date()}
            onSave={async (unixTimestamp) => {
              if (!trialModal.reason.trim()) {
                setTrialModal((m) => ({ ...m, error: t('orgTrialModal.errorRequired') }));
                return;
              }
              setTrialModal((m) => ({ ...m, saving: true, error: '' }));
              try {
                const iso = new Date(unixTimestamp * 1000).toISOString();
                const res = await setTrialEnd(trialModal.orgId, { endsAt: iso, reason: trialModal.reason });
                if (res?.stripe_sync && res.stripe_sync.success === false) {
                  throw new Error(res?.stripe_sync?.reason || res?.stripe_sync?.error || 'Stripe sync failed');
                }
                setTrialModal(null);
                const trialToast =
                  res?.stripe_sync?.stripe_trial_update_skipped
                    ? (t('orgActions.extendStripeTrialDbOnlyToast') || res?.stripe_sync?.hint || '')
                    : (t('common.saved') || 'Saved');
                setToast({ msg: trialToast, err: false });
                await load();
              } catch (e) {
                const detail = e?.data?.detail || e?.data?.stripe_sync?.error || e?.data?.stripe_sync?.reason || null;
                setTrialModal((m) => ({ ...m, error: detail || e?.data?.error || e.message || 'Error', saving: false }));
              }
            }}
            onCancel={() => setTrialModal(null)}
            disabled={trialModal.saving}
            error={trialModal.error}
            reason={trialModal.reason}
            onReasonChange={e => setTrialModal((m) => ({ ...m, reason: e.target.value }))}
          />
        </ModalShell>
      )}

      {endTrialModal && (
        <ModalShell
          title={t('orgActions.endStripeTrial') || 'Terminar trial en Stripe'}
          onClose={() => endTrialModal.saving ? null : setEndTrialModal(null)}
          footer={(
            <>
              <button
                className="ui-button ghost"
                onClick={() => setEndTrialModal(null)}
                disabled={endTrialModal.saving}
              >
                {t('actions.cancel') || 'Cancelar'}
              </button>
              <button
                className="ui-button danger"
                onClick={async () => {
                  if (!endTrialModal?.orgId || endTrialModal.saving) return;
                  setEndTrialModal((m) => ({ ...m, saving: true, error: '' }));
                  try {
                    const res = await endStripeTrial(endTrialModal.orgId, { reason: (endTrialModal.reason || '').trim() });
                    if (res?.stripe_sync && res.stripe_sync.success === false) {
                      throw new Error(res?.stripe_sync?.reason || res?.stripe_sync?.error || 'Stripe sync failed');
                    }
                    const updated = res?.organization || res;
                    if (updated) upsertOrg(updated);
                    setEndTrialModal(null);
                    setToast({ msg: t('orgActions.endStripeTrialToast') || 'Trial terminado en Stripe', err: false });
                    await load();
                  } catch (e) {
                    const detailErr = e?.data?.detail || e?.data?.stripe_sync?.error || e?.data?.stripe_sync?.reason || null;
                    setEndTrialModal((m) => ({
                      ...m,
                      saving: false,
                      error: detailErr || e?.data?.error || e?.message || 'Error'
                    }));
                  }
                }}
                disabled={endTrialModal.saving}
              >
                {endTrialModal.saving ? (t('common.loading') || 'Cargando...') : (t('orgActions.endStripeTrial') || 'Terminar trial')}
              </button>
            </>
          )}
        >
          <p className="ui-subtext" style={{ marginBottom: 12 }}>
            {(t('orgActions.endStripeTrialConfirm') || 'Terminar trial en Stripe ahora para {name}?').replace('{name}', endTrialModal.orgName || 'esta organización')}
          </p>
          <div className="ui-field">
            <label className="ui-label">{t('org.planReasonLabel') || 'Motivo (opcional)'}</label>
            <textarea
              className="ui-textarea"
              value={endTrialModal.reason}
              onChange={(e) => setEndTrialModal((m) => ({ ...m, reason: e.target.value }))}
              placeholder={t('billingOverride.promptReason') || 'Motivo (opcional)'}
              disabled={endTrialModal.saving}
            />
          </div>
          {endTrialModal.error && (
            <div className="sa-toast sa-toast--error" role="alert" style={{ position: 'relative', marginTop: 8 }}>
              {endTrialModal.error}
            </div>
          )}
        </ModalShell>
      )}

      {createModal && (
        <ModalShell
          title={t('org.createNew') || 'Crear organización'}
          width="720px"
          onClose={() => createModal.saving ? null : setCreateModal(null)}
          footer={(
            <>
              <button className="ui-button ghost" onClick={() => setCreateModal(null)} disabled={createModal.saving}>
                {t('actions.cancel')}
              </button>
              <button
                className="ui-button primary"
                onClick={async () => {
                  if (!createModal.name.trim()) {
                    setCreateModal((m) => ({ ...m, error: t('org.errorNameRequired') || 'Nombre requerido' }));
                    return;
                  }
                  if (!createModal.admin_email?.trim()) {
                    setCreateModal((m) => ({ ...m, error: t('org.errorEmailRequired') || 'Email requerido' }));
                    return;
                  }
                  if (!createModal.admin_password || createModal.admin_password.length < 6) {
                    setCreateModal((m) => ({ ...m, error: t('org.errorPasswordRequired') || 'Contraseña mínima 6 caracteres' }));
                    return;
                  }
                  if (createModal.admin_password !== createModal.admin_password2) {
                    setCreateModal((m) => ({ ...m, error: t('org.errorPasswordMatch') || 'Las contraseñas no coinciden' }));
                    return;
                  }
                  setCreateModal((m) => ({ ...m, saving: true, error: '' }));
                  try {
                    await createOrg({
                      name: createModal.name.trim(),
                      billing_tier: createModal.billing_tier,
                      admin_email: createModal.admin_email.trim(),
                      admin_password: createModal.admin_password,
                      admin_name: createModal.admin_name?.trim()
                    });
                    setCreateModal(null);
                    setToast({ msg: t('org.created') || 'Organización creada', err: false });
                    setPage(1);
                    await load();
                  } catch (e) {
                    setCreateModal((m) => ({
                      ...m,
                      saving: false,
                      error: e?.data?.detail || e?.data?.error || e.message || 'Error'
                    }));
                  }
                }}
                disabled={createModal.saving}
              >
                {createModal.saving ? t('common.loading') : t('actions.save')}
              </button>
            </>
          )}
        >
          <div className="ui-field">
            <label className="ui-label">{t('org.nameLabel') || 'Nombre'}</label>
            <input
              className="ui-input"
              value={createModal.name}
              onChange={(e) => setCreateModal((m) => ({ ...m, name: e.target.value }))}
              placeholder={t('org.namePlaceholder') || 'Ej. Cliente ACME'}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.adminName') || 'Nombre admin'}</label>
            <input
              className="ui-input"
              value={createModal.admin_name}
              onChange={(e) => setCreateModal((m) => ({ ...m, admin_name: e.target.value }))}
              placeholder={t('org.adminNamePlaceholder') || 'Nombre de la persona admin'}
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.adminEmail') || 'Email admin'}</label>
            <input
              className="ui-input"
              type="email"
              value={createModal.admin_email}
              onChange={(e) => setCreateModal((m) => ({ ...m, admin_email: e.target.value }))}
              placeholder="admin@ejemplo.com"
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.adminPassword') || 'Contraseña inicial'}</label>
            <input
              className="ui-input"
              type="password"
              value={createModal.admin_password}
              onChange={(e) => setCreateModal((m) => ({ ...m, admin_password: e.target.value }))}
              placeholder="Mínimo 6 caracteres"
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.adminPasswordConfirm') || 'Confirmar contraseña'}</label>
            <input
              className="ui-input"
              type="password"
              value={createModal.admin_password2}
              onChange={(e) => setCreateModal((m) => ({ ...m, admin_password2: e.target.value }))}
              placeholder="Repite la contraseña"
            />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('org.planLabel') || 'Plan'}</label>
            <select
              className="ui-input"
              value={createModal.billing_tier}
              onChange={(e) => setCreateModal((m) => ({ ...m, billing_tier: e.target.value }))}
            >
              {planOptions.map(({ value, limit }) => (
                <option key={value} value={value}>
                  {`${safeT(`org.planLabelMap.${value}`, value)} (${formatSeatLimit(limit)})`}
                </option>
              ))}
            </select>
          </div>
          {createModal.error && <div className="error-box">{createModal.error}</div>}
        </ModalShell>
      )}
    </div>
  );
}
